import { useEffect, useState } from 'react';

import Highcharts from 'highcharts';
import { HighchartsChart, HighchartsProvider, XAxis, YAxis, Series } from 'react-jsx-highcharts';
import StatInfo from './StatInfo';
import styled from 'styled-components';
import { shallowEqual, useSelector } from 'react-redux';
import store from 'store';
import { getQueryCampaignId, getQueryEmailId, getQueryEmailIds, getQueryIsAIEmail } from 'modules/EmailBrowser/utils';
import { getConstantCampaignEmailId } from '../utils';
import { ANALYTICS_DEFAULT_DATES_FILTER } from '../Contacts/utils';
import { addCommaToNumber, getQuarterDates, truncateFrontZeros } from 'utils';
import { analyticsDatesFilterChange, selectAsset, setFirstTimeAllDataShown } from '../Dashboard/actions';
import { moveSelectedFiltersToApplied } from '../actions';
import { ANALYTICS_FILTERS } from 'constants/types';
import EmailTrendsLoader from '../Shared/EmailTrendsLoader';
import { ANALYTICS_EMAIL_TRENDS, ANALYTICS_CLICK_TRENDS } from './../../../constants/types/analyticsTypes';
import { resetAllTabsFilter } from 'modules/Analytics/utils';

// Cache the default values to restore when this example unmounts
const oldReset = Highcharts.Pointer.prototype.reset;
const oldHighlight = Highcharts.Point.prototype.highlight;

//const colors = ["#001760", "#1890FF", "#FF9531", "#1EC252", "#CF1322", "#F5222D"];
const quarterMap = {
  Jan: '1Q',
  Feb: '1Q',
  Mar: '1Q',
  Apr: '2Q',
  May: '2Q',
  Jun: '2Q',
  Jul: '3Q',
  Aug: '3Q',
  Sep: '3Q',
  Oct: '4Q',
  Nov: '4Q',
  Dec: '4Q',
};
const monthMap = {
  Jan: 1,
  Feb: 2,
  Mar: 3,
  Apr: 4,
  May: 5,
  Jun: 6,
  Jul: 7,
  Aug: 8,
  Sep: 9,
  Oct: 10,
  Nov: 11,
  Dec: 12,
};
function StackedBarChart(params) {
  const { getTrends } = params;
  const [chartData, setChartData] = useState(null);
  const [chartTitle, setChartTitle] = useState('');
  const [loaderType, setLoaderType] = useState('');
  const [loader, setLoader] = useState(true);
  const [increaseFlag, setIncreaseFlag] = useState(true);
  const [decreaseFlag, setDecreaseFlag] = useState(true);
  const loaderArray = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  const isFilterClicked = useSelector((state) => state.analyticsFilters.filterApplied);
  const AllCampaignsFilterCheck = useSelector((state) => state.analyticsDashboard.appliedCampaignsFilters.isAllCampaigns);
  const { isShareProfile, isDailyIQ, isWeeklyAccountInsights } = useSelector((state) => state.analyticsDashboard.appliedCampaignsFilters);
  const [firstRender, setFirstRender] = useState(true);

  const formatLabel = (dateRange) => {
    //incoming format
    // const dummy = "20-Jul-2022 to 31-Jul-2022";
    const dateArr = dateRange.split('to');
    const fromDate = dateArr[0].split('-').map((val) => truncateFrontZeros(val.trim()));
    let index;
    let zoomLevel;
    if (loaderType === 'clickchart') {
      index = store.getState().clickTrends.zoomLevelIndex;
      zoomLevel = store.getState().clickTrends.zoomLevel[index];
    } else {
      index = store.getState().emailTrends.zoomLevelIndex;
      zoomLevel = store.getState().emailTrends.zoomLevel[index];
    }

    let formattedDate = dateRange;
    if (dateArr.length === 2) {
      switch (zoomLevel) {
        case 'weekly':
          //Weekly > 8/9 [month/date]
          formattedDate = `${monthMap[fromDate[1]]}/${fromDate[0]}`;
          break;
        case 'monthly':
          // Monthly > Jun, 22;
          formattedDate = `${fromDate[1]}, ${fromDate[2].trim().substr(fromDate[2].trim().length - 2)}`;
          break;
        case 'quarterly':
          // quarterly > 1Q, 2022
          formattedDate = `${quarterMap[fromDate[1]]}, ${fromDate[2].trim()}`;
          break;
        case 'yearly':
          // yearly > 2022, 2021
          formattedDate = `${fromDate[2].trim()}`;
          break;
        default:
          break;
      }
    } else if (dateArr.length === 1) {
      if (zoomLevel === 'daily') {
        //daily > 8/9 [month/date]
        formattedDate = `${monthMap[fromDate[1]]}/${fromDate[0]}`;
      }
    }

    return formattedDate.trim();
  };
  const selectedCampaignIds = useSelector((state) => {
    return state.analyticsDashboard.selectedCampaignsIds;
  }, shallowEqual);
  const chartObjs = {};

  const getParams = () => {
    let analyticsDashboard = store.getState().analyticsDashboard;
    let analyticsGraph = store.getState().analyticsGraph;
    let { appliedCampaignIds, appliedAssetsIds, excludeCampaignsIds, excludeAssetsIds, appliedWorkflowsIds } = analyticsDashboard;
    let { startDate, endDate } = analyticsGraph;

    let campaignIds = appliedCampaignIds;
    let emailIds = appliedAssetsIds;

    let params = {};

    const queryCampaignId = getQueryCampaignId();
    const isAIEmail = getQueryIsAIEmail();

    if (selectedCampaignIds.length <= 0 && getQueryCampaignId() > 0 && firstRender === true) {
      campaignIds = appliedCampaignIds.length > 0 ? appliedCampaignIds : [getQueryCampaignId()];
      if (isAIEmail) {
        const emailIds = getQueryEmailIds();
        emailIds.forEach((id) => {
          selectAsset(id, queryCampaignId);
        });
      } else {
        selectAsset(getQueryEmailId(), queryCampaignId);
      }
      moveSelectedFiltersToApplied();
    }

    if (appliedAssetsIds.length <= 0 && getQueryEmailId() > 0 && firstRender === true) {
      if (isAIEmail) {
        emailIds = appliedAssetsIds.length > 0 ? appliedAssetsIds : getQueryEmailIds();
      } else {
        emailIds = appliedAssetsIds.length > 0 ? appliedAssetsIds : [getQueryEmailId()];
      }
    }

    if (startDate === '' && endDate === '') {
      analyticsDatesFilterChange(ANALYTICS_DEFAULT_DATES_FILTER);
      startDate = getQuarterDates().start_date;
      endDate = getQuarterDates().end_date;
    }
    // check if is share profile || daily IQ || weekly accounts insights
    if (isShareProfile || isDailyIQ || isWeeklyAccountInsights) {
      let campaign_email_id = getConstantCampaignEmailId({ isShareProfile, isDailyIQ, isWeeklyAccountInsights });
      campaignIds = [campaign_email_id];
      emailIds = [campaign_email_id];
    }

    params = {
      startDate: startDate,
      endDate: endDate,
      allCampaigns: AllCampaignsFilterCheck,
      emailIds: emailIds,
      campaignIds: campaignIds,
      workflowIds: appliedWorkflowsIds,
    };

    if (AllCampaignsFilterCheck) {
      params['excludeCampaignsIds'] = excludeCampaignsIds;
      params['excludeAssetsIds'] = excludeAssetsIds;
    }
    return params;
  };

  //To increase the zoom level
  const increaseZoomLevel = (e) => {
    e.persist();
    setLoader(true);
    let index = 0;
    if (loaderType === 'clickchart') {
      index = store.getState().clickTrends.zoomLevelIndex + 1;
      store.dispatch({ type: ANALYTICS_CLICK_TRENDS.UPDATE_ZOOMLEVEL, payload: index });
    } else {
      index = store.getState().emailTrends.zoomLevelIndex + 1;
      store.dispatch({ type: ANALYTICS_EMAIL_TRENDS.UPDATE_ZOOMLEVEL, payload: index });
    }
    changeChartData();
  };

  // To decrease the zoom level
  const decreaseZoomLevel = (e) => {
    e.persist();
    setLoader(true);
    let index = 0;
    if (loaderType === 'clickchart') {
      index = store.getState().clickTrends.zoomLevelIndex - 1;
      store.dispatch({ type: ANALYTICS_CLICK_TRENDS.UPDATE_ZOOMLEVEL, payload: index });
    } else {
      index = store.getState().emailTrends.zoomLevelIndex - 1;
      store.dispatch({ type: ANALYTICS_EMAIL_TRENDS.UPDATE_ZOOMLEVEL, payload: index });
    }
    changeChartData();
  };

  const buttonDisabledFlag = (type) => {
    store.getState()[type].zoomLevelIndex === store.getState()[type].zoomLevel.length - 1 ? setIncreaseFlag(true) : setIncreaseFlag(false);
    store.getState()[type].zoomLevelIndex === 0 ? setDecreaseFlag(true) : setDecreaseFlag(false);
  };

  const changeChartData = () => {
    let trendsData;
    let title;
    let level = [];
    if (window.location.pathname == '/analytics/trends/clickTrends/') {
      level = store.getState().clickTrends.zoomLevel;
      trendsData = store.getState().clickTrends[level[store.getState().clickTrends.zoomLevelIndex]];
      title = store.getState().clickTrends.title;
      setLoaderType('clickchart');
      buttonDisabledFlag('clickTrends');
    } else {
      level = store.getState().emailTrends.zoomLevel;
      trendsData = store.getState().emailTrends[level[store.getState().emailTrends.zoomLevelIndex]];
      title = store.getState().emailTrends.title;
      setLoaderType('');
      buttonDisabledFlag('emailTrends');
    }
    setChartTitle(title);
    let dataSetsCount = trendsData?.yAxis?.length;
    let datasets = trendsData?.yAxis?.map((obj, index) => {
      return {
        name: obj.name,
        data: obj.values,
        type: 'column',
        valueDecimals: 1,
        value: obj.total,
        color: obj.color,
        isLastDataset: dataSetsCount - 1 === index ? true : false,
      };
    });
    const firstTimeFilterShown = store.getState().analyticsDashboard.firstAllDataShown;
    if (!firstTimeFilterShown) {
      setFirstTimeAllDataShown(true);
    }
    let xData = trendsData?.xAxis;
    if (datasets && xData) {
      setChartData({
        datasets,
        xData,
      });
    }
  };

  const configureChart = () => {
    return getTrends(getParams()).then(() => {
      Highcharts.Pointer.prototype.reset = () => {};
      setLoader(true);
      changeChartData();
    });
  };

  useEffect(() => {
    if (isFilterClicked) {
      setLoader(true);
      configureChart().then(() => {
        store.dispatch({
          type: ANALYTICS_FILTERS.RESET,
        });
        resetAllTabsFilter(true);
      });
    }
  }, [isFilterClicked]);

  useEffect(() => {
    setLoader(true);
    configureChart().then(() => {
      setFirstRender(false);
    });
    return () => {
      // Restore the cached defaults
      Highcharts.Pointer.prototype.reset = oldReset;
      Highcharts.Point.prototype.highlight = oldHighlight;
    };
  }, [getTrends]);

  useEffect(() => {
    if (chartData !== null) {
      setLoader(false);
    }
  }, [chartData]);

  const renderChart = (dataset, index) => {
    const data = dataset.data.map((val, i) => [chartData.xData[i], val]);

    return (
      <ChartWrapper className="chart-cls">
        <StatInfo number={dataset.value} description={dataset.name} />
        <HighchartsProvider Highcharts={Highcharts}>
          <HighchartsChart
            key={dataset.name}
            chart={{
              events: {
                load: function () {
                  const tooltip = this.tooltip;
                  chartObjs[dataset.name] = tooltip;
                },
              },

              point: {
                events: {
                  mouseOut: function () {
                    this.chart.tooltip.hide(0);
                  },
                },
              },
              height: loaderType == 'clickchart' ? (dataset.isLastDataset ? 75 : 40) : dataset.isLastDataset ? 100 : 65, //dataset.isLastDataset ? 166 : 108,
              width: 1440, //width: loaderType === "clickchart" ? 1390 : 1000,
              // viewBox: "0 0 1000 400",
              // borderWidth: 1,
              plotBorderWidth: 1,
              spacingTop: 1,
              spacingRight: 0,
              spacingBottom: 1,
              spacingLeft: 0,
              // plotBorderWidth: 0,
              marginRight: 0, //-60, //this does move the chart but you'll need to recompute it
              marginLeft: 0, //-60,  //whenever the page changes width
            }}
            plotOptions={{
              series: {
                point: {
                  events: {
                    mouseOver: function (e) {
                      Object.keys(chartObjs).forEach((key) => {
                        if (key !== dataset.name) {
                          chartObjs[key].hide(0);
                        }
                      });
                    },
                  },
                  mouseOut: function (e) {
                    this.chart.tooltip.hide(0);
                  },
                },
                stickyTracking: false,
                events: {
                  mouseOut: function () {
                    this.chart.tooltip.hide(0);
                  },
                },
              },
              // series: {
              //   minPointLength: 3,
              //   //   // borderWidth: 2,
              //   //   // borderColor: "black",
              // },

              bar: {
                borderRadius: 3,
              },
              column: {
                borderRadius: 3,
                pointPadding: 0,
                borderWidth: 0,
                groupPadding: 0,
                shadow: false,
                dataLabels: {
                  enabled: true,
                  crop: false,
                  overflow: 'justify',
                  align: 'center',
                  style: {
                    color: 'black',
                    textOutline: 'none',
                  },
                  formatter: function () {
                    let datasetsName = ['Open Rate', 'Click Rate', 'Bounce Rate'];
                    if (datasetsName.includes(dataset.name)) {
                      return this.y.toString() + '%';
                    } else {
                      let labelFormat = new Intl.NumberFormat('en-US');
                      return labelFormat.format(this.y);
                    }
                  },
                },
              },
            }}
            tooltip={{
              shared: true,
              animation: false,
              followPointer: true,
              useHTML: true,
              hideDelay: 0,
              outside: true,
              positioner: function (labelWidth, labelHeight, point) {
                return {
                  x: this.chart.container.getBoundingClientRect().left + point.plotX - (window.devicePixelRatio > 1 ? 87 : 122) - 10,
                  // y: this.chart.container.getBoundingClientRect().top - (window.devicePixelRatio > 1 ? 45 : 80),
                  y: this.chart.container.getBoundingClientRect().top + point.plotY - (window.devicePixelRatio > 1 ? 50 : 85) - 10,
                };
              },
              // distance: 50,
              // followPointer: true,
              formatter: function (obj, tooltip) {
                //style="color: white; width: 230px;"
                const header = `<div >
                <span style="color: #7E7E7E; font-weight: 700; font-size: 16px; font-family: Roboto Slab;">${this.points[0].key}</span>
                <div>
                <span style="height: 8px; width: 8px; background-color: ${this.points[0].color}; border-radius: 50%; display: inline-block"></span>
                <span style="color: #424B50; font-weight: 700; font-size: 16px; font-family: Roboto Slab;">${dataset.name + ':'}</span>
                <span style="color: #424B50; font-weight: 700; font-size: 16px; font-family: Roboto Slab;">${addCommaToNumber(this.y.toString())}${
                  dataset.name.toLowerCase().includes('rate') ? ' %' : ''
                }</span>
                </div>
                </div>`;

                return header;
              },
            }}
            xAxis={{
              visible: dataset.isLastDataset ? true : false,
              gridLineWidth: 0,
              minorGridLineWidth: 0,
              lineWidth: 0,
              lineColor: 'transparent',
              minorTickLength: 0,
              tickLength: 0,
              minPadding: 0,
              maxPadding: 0,
              labels: {
                // useHTML: true,
                // style: {
                //   paddingTop: "1px",
                // },
                formatter: function (obj) {
                  return formatLabel(chartData.xData[obj.pos]);
                },
              },
            }}
            yAxis={{
              visible: false,
              // gridLineColor: "transparent",
              // min: -0.1, //
              labels: {
                enabled: false,
              },
              // title: {
              //   text: "Rainfall (mm)",
              // },
              gridLineWidth: 0,
            }}
            exporting={{ enabled: false }}
          >
            <XAxis crosshair labels={{ format: '{value} km' }} />
            <YAxis>
              <Series name={dataset.name} type={dataset.type} data={data} color={dataset.color} tooltip={{ valueSuffix: ` ${dataset.unit}` }} />
            </YAxis>
          </HighchartsChart>
        </HighchartsProvider>
      </ChartWrapper>
    );
  };

  const handleMouseMove = (e) => {
    let point = null;
    let event = null;
    e.persist();
    Highcharts.charts.forEach((chart) => {
      if (!chart) return;
      event = chart.pointer.normalize(e); // Find coordinates within the chart
      point = chart?.series[0]?.searchPoint(event, true); // Get the hovered point
      // if (point) {
      //   point.highlight(e);
      // }
    });
  };
  return (
    <Wrapper>
      <div className="heading top-section">
        {loader ? null : chartTitle}
        {!loader ? (
          <div className="zoom-section">
            <button disabled={increaseFlag} onClick={increaseZoomLevel}>
              <span>+</span>
              <span className="pipe"></span>
            </button>
            <button disabled={decreaseFlag} onClick={decreaseZoomLevel}>
              <span>-</span>
            </button>
          </div>
        ) : null}
      </div>

      {loader ? (
        loaderArray.map((item, index) => <EmailTrendsLoader type="clickchart" />)
      ) : (
        <div>
          <SyncedChartsWrapper onMouseMove={handleMouseMove} onTouchMove={handleMouseMove} className="chart-container">
            {chartData?.datasets?.map(renderChart)}
          </SyncedChartsWrapper>
          {/* <ExportAnalyticsPopup page={"insights"} tab={loaderType === "clickchart" ? "clickTrends" : ""} /> */}
        </div>
      )}
    </Wrapper>
  );
}

export default StackedBarChart;

const ChartWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  /*margin-top: 5px;
  margin-bottom: 5px;
  padding-top: 5px;*/
  .chart {
    /* border: 1px solid rgba(126, 126, 126, 0.4); */
  }
`;

const SyncedChartsWrapper = styled.div`
  background: white;
`;

const Wrapper = styled.div`
  background: white;
  padding: 0px 24px 15px;
  border-radius: 10px;
  margin-bottom: 15px;
  box-shadow: 0px 0px 17px -2px rgb(0 0 0 / 8%);

  .heading {
    color: #424b50;
    font-family: 'Roboto Slab';
    font-weight: 500;
    font-size: 22px;
    letter-spacing: 0.7px;
    line-height: 1;
    padding-bottom: 17px;
    padding-top: 28px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .zoom-section {
    border: 1px solid #dadada;
    max-height: 26px;
    border-radius: 6px;
    min-width: 52px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 5px;
    button {
      border: none;
      background: none;
      display: inline-flex;
      align-items: center;
      color: #7e7e7e;
      font-size: 20px;
      box-shadow: none;
      outline: none;
      padding: 0;
    }
    button:disabled {
      cursor: not-allowed;
    }
    .pipe {
      display: inline-block;
      border-right: 1px solid #dadada;
      height: 15px;
      margin-left: 9px;
    }
  }
  .highcharts-yaxis-labels {
    display: none;
  }
  .highcharts-grid-line {
    display: none;
  }
  .highcharts-axis-line {
    display: none;
  }
  .chart-cls:last-child {
    .data-chart {
      padding-bottom: 28px;
    }
  }
  .chart-cls:last-child .highcharts-container svg > g:nth-last-child(3) {
    transform: translate(0px, 15px);
    /* text {
      y: 130;
      x: 118;
    } */
  }
  .chart-cls .chart {
    width: calc(100% - 150px);
    .highcharts-container {
      width: 100% !important;
      svg {
        width: 100% !important;
        .highcharts-plot-border {
          width: 100%;
          x: 0;
        }
      }
    }
  }
  .highcharts-root {
    .highcharts-tooltip {
      /* display: none !important; */
    }
  }
  .chart-cls {
    .highcharts-tooltip {
      .highcharts-label {
        > span {
          > div {
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              width: auto !important;
            }
            span {
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                width: auto !important;
                font-size: 10px !important;
              }
            }
          }
        }
      }
    }
  }
`;
